import { createRoute, redirect } from "@tanstack/solid-router";

import { rootRoute } from "../__root";
import { sessionActor } from "@/lib/session-machine";
import { accountsStore, readAccountsStateSync } from "@/lib/accounts";
import { buildUPath, outOfRangeRedirectIndex, stripUPrefix } from "@/lib/active-account";
import { api, fetchApi } from "@/lib/api";
import { queryClient } from "@/lib/query-client";
import {
  cloneSession,
  type components,
  HTTP_FORBIDDEN,
  HTTP_UNAUTHORIZED,
  isErrorModel,
  isStatusError,
  isValidSession,
  queryKeys,
  type Session,
} from "@mealplan/shared";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { AppLayoutContent } from "@/features/layout/components/app-layout-content";
import { validateAppLayoutSearch } from "@/features/layout/app-search";
import { SESSION_STALE_TIME_MS } from "@/lib/constants";

function AppLayout() {
  const { db } = usePowerSyncContext();

  // Render the static shell immediately, even before PowerSync's local DB has
  // finished initializing. `db` may be null on first paint; AppLayoutContent
  // shows granular per-region skeletons until it becomes available.
  return <AppLayoutContent db={db()} />;
}

// Deduplicates concurrent beforeLoad calls during the page-load preload burst.
// When viewport preloads fire simultaneously for multiple child routes, they all
// share a single API request instead of each sending their own.
let activeSessionCheck: Promise<void> | null = null;

// Module-level helper so its type can be inferred without a local const.
const fetchSession = () => api.GET("/api/auth/get-session", { params: { query: { jwt: true } } });
type FetchResult = Awaited<ReturnType<typeof fetchSession>>;

async function runSessionCheck(
  snapshot: ReturnType<typeof sessionActor.getSnapshot>,
): Promise<void> {
  // Wait for the machine to finish priming from storage.
  if (snapshot.matches("priming")) {
    await new Promise<void>((resolve) => {
      const sub = sessionActor.subscribe((s) => {
        if (!s.matches("priming")) {
          sub.unsubscribe();
          resolve();
        }
      });
    });
  }

  const currentSnapshot = sessionActor.getSnapshot();
  // Machine may have recovered a session from localStorage during priming.
  // If we have a session OR we have already verified with the server, we skip.
  if (currentSnapshot.context.session || currentSnapshot.context.isVerified) {
    return;
  }

  // No session in machine — verify with the server once.
  // The redirect is thrown outside try/catch so TanStack Router receives it.
  const sessionResult = await (async () => {
    try {
      // Add a timeout to avoid hanging the route transition if the server is slow/rate-limited
      const timeoutPromise = new Promise<FetchResult>((_resolve, reject) => {
        const SESSION_CHECK_TIMEOUT = 1500;
        setTimeout(() => {
          reject(new Error("Session check timeout"));
        }, SESSION_CHECK_TIMEOUT);
      });

      return await Promise.race([
        api.GET("/api/auth/get-session", {
          params: { query: { jwt: true } },
        }),
        timeoutPromise,
      ]);
    } catch (err) {
      console.warn("[SessionCheck] Error o timeout al verificar sesión:", err);
      return null;
    }
  })();

  if (!sessionResult) {
    // Network error / timeout — DO NOT send VERIFY_COMPLETE. Marking the
    // machine as verified would lock the app in visitor mode permanently,
    // because every subsequent route load would early-return on `isVerified`
    // and the in-component `useSession` query is enabled by `!isPriming()`
    // alone. Leaving `isVerified: false` lets the next navigation retry
    // and `useSession` retry on mount.
    return;
  }

  const { data, error, response } = sessionResult;

  // Sync with machine regardless of success/error (error will clear session)
  let sessionPayload: Session | null = null;
  if (isValidSession(data)) {
    sessionPayload = cloneSession(data);
  }

  let errorPayload: components["schemas"]["ErrorModel"] | null = null;
  if (isErrorModel(error)) {
    errorPayload = error;
  }

  sessionActor.send({
    type: "SERVER_REPLY",
    session: sessionPayload,
    error: errorPayload,
  });

  if (error) {
    const status = isStatusError(error) ? error.status : response.status;
    if (status === HTTP_UNAUTHORIZED || status === HTTP_FORBIDDEN) {
      // Server explicitly rejected auth — allow access as a visitor/guest.
      // (SERVER_REPLY above already handled this, but we return early)
      return;
    }
    // Other server error — allow offline access.
    // Ensure we are marked as verified if the above SERVER_REPLY was skipped or didn't set it.
    // (Actually SERVER_REPLY always sets isVerified: true in our machine)
    return;
  }

  queryClient.setQueryData(queryKeys.auth.session(), data);
}

export const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  // Per-tab multi-account: the whole app tree lives under a `/u/<n>` prefix
  // where `<n>` is the 0-based index into the accounts registry. `<n>` is part
  // of the URL so two browser tabs (each its own `location`) can hold different
  // active accounts simultaneously, Gmail-style (`/mail/u/0/…`).
  //
  // This route's id is therefore `/u/$u` (TanStack derives it from `path`, and
  // a route may NOT set both `id` and `path`). Children's ids compose to
  // `/u/$u/recipes`, … and `useSearch`/`useParams` use `from: "/u/$u"`.
  path: "/u/$u",
  // Parse `$u` to a non-negative integer; reject anything else so a bogus
  // `/u/abc` 404s into the not-found component rather than rendering the app
  // with a NaN index.
  params: {
    parse: (raw: Record<string, string>) => {
      const index = Number.parseInt(raw.u ?? "", 10);
      if (!Number.isInteger(index) || index < 0 || String(index) !== raw.u) {
        throw new Error(`Invalid account index: ${raw.u}`);
      }
      return { u: index };
    },
    stringify: (params: { u: number }) => ({ u: String(params.u) }),
  },
  // Search-state global del layout `_app` (multi-cuenta: `?account=switch`).
  // Da URL propia al overlay del switcher para que sea copiable/reabrible.
  validateSearch: validateAppLayoutSearch,
  beforeLoad: ({ params, location }) => {
    // BUG B: a `/u/<n>` index that does NOT exist in the registry must redirect
    // to a VALID index, never `clampIndex`-render a phantom (the guest, or the
    // wrong account). `params.u` is already a parsed non-negative integer.
    const accountsState = readAccountsStateSync();
    if (accountsState.accounts.length === 0) {
      // Deep-link into `/u/<n>` before the guest exists: mint it (idempotent,
      // lands at index 0) — mirroring the root bare-path redirect — and bounce
      // any non-zero index onto `/u/0` instead of rendering an empty phantom.
      void accountsStore.ensureGuestActive();
      if (params.u !== 0) {
        // oxlint-disable-next-line @typescript-eslint/only-throw-error
        throw redirect({
          to: buildUPath(0, stripUPrefix(location.pathname)),
          search: location.search as Record<string, unknown>,
          replace: true,
        });
      }
    } else {
      const target = outOfRangeRedirectIndex(accountsState, params.u);
      if (target !== null && target !== params.u) {
        // oxlint-disable-next-line @typescript-eslint/only-throw-error
        throw redirect({
          to: buildUPath(target, stripUPrefix(location.pathname)),
          search: location.search as Record<string, unknown>,
          replace: true,
        });
      }
    }

    const snapshot = sessionActor.getSnapshot();

    // Hot path: session already established and machine is done priming.
    // We only prefetch if the cached data is stale — useSession's createQuery
    // will handle refetching reactively once mounted.
    if (!snapshot.matches("priming") && snapshot.context.session) {
      const cached = queryClient.getQueryState(queryKeys.auth.session());
      const isStale =
        !cached?.dataUpdatedAt || Date.now() - cached.dataUpdatedAt > SESSION_STALE_TIME_MS;

      if (isStale) {
        void queryClient.prefetchQuery({
          queryKey: queryKeys.auth.session(),
          queryFn: async () =>
            fetchApi(api.GET("/api/auth/get-session", { params: { query: { jwt: true } } })),
          staleTime: SESSION_STALE_TIME_MS,
        });
      }
      return;
    }

    // Cold path: fire the verify in the background and let the route render
    // immediately. The InitialSyncOverlay covers the UI while the machine is
    // priming/verifying, and the AppLayout component reactively redirects to
    // /login if the verify resolves to an unauthenticated user. This matches
    // the behaviour of `apps/native`: routing is never gated on a network
    // round-trip, and the user never sees a frozen login form.
    activeSessionCheck ??= (async () => {
      try {
        await runSessionCheck(snapshot);
      } finally {
        activeSessionCheck = null;
      }
    })();
    // Intentional fire-and-forget — see comment above.
  },

  component: AppLayout,
});
